import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, CreditCard, Calendar, DollarSign, Trash2, Eye, EyeOff, AlertTriangle, CheckCircle } from 'lucide-react';
import useDemoDates from '@/hooks/useDemoDates';
import AddDateDialog from './AddDateDialog';
import { AddToCalendarButton } from '@/components/native/ContactActionButtons';

const MembershipsTab = () => {
  const { dates, loading, addDate, deleteDate } = useDemoDates('membership');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this membership? You can always add it again later.')) return;
    await deleteDate(id);
  };

  const handleDateAdded = async (dateData) => {
    await addDate(dateData);
    setDialogOpen(false);
  };

  const togglePassword = (id) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getStatusBadge = (status) => {
    const config = {
      trial: { label: 'Trial', color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle },
      active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: null }
    };
    const cfg = config[status] || config.active;
    return (
      <Badge className={cfg.color}>
        {cfg.icon && <cfg.icon className="h-3 w-3 mr-1" />}
        {cfg.label}
      </Badge>
    );
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(dateStr + 'T00:00:00');
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">Memberships & Subscriptions</h3>
          <p className="text-sm text-gray-500">Track trials, renewals, and subscriptions</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-[#C5D9ED] hover:bg-[#B0CCE3] text-gray-900 whitespace-nowrap flex-shrink-0"
          data-testid="add-membership-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Membership
        </Button>
      </div>

      {dates.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <CreditCard className="h-12 w-12 mx-auto text-[#C5D9ED] mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">Track Your Subscriptions</h4>
          <p className="text-gray-600 mb-4">
            Keep track of trials, renewals, and subscriptions so nothing slips through
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-[#C5D9ED] hover:bg-[#B0CCE3] text-gray-900"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Membership
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {dates.map((item) => {
            const trialDaysLeft = getDaysUntil(item.trial_end_date);
            const renewalDaysLeft = getDaysUntil(item.renewal_date);
            const isTrialEnding = trialDaysLeft !== null && trialDaysLeft <= 7 && trialDaysLeft >= 0;
            const isRenewalSoon = renewalDaysLeft !== null && renewalDaysLeft <= 7 && renewalDaysLeft >= 0;
            
            return (
              <Card key={item.id} className={`border-l-4 ${isTrialEnding || isRenewalSoon ? 'border-l-orange-400' : 'border-l-[#C5D9ED]'}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h4 className="font-medium text-gray-900">{item.title}</h4>
                        {item.membership_status && getStatusBadge(item.membership_status)}
                        {item.membership_category && (
                          <Badge variant="outline" className="capitalize">
                            {item.membership_category}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Alerts for trial ending or renewal */}
                      {isTrialEnding && (
                        <div className="mb-2 p-2 bg-orange-50 rounded border border-orange-200 text-orange-700 text-sm">
                          <AlertTriangle className="h-4 w-4 inline mr-1" />
                          Trial ends in {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''}!
                        </div>
                      )}
                      {isRenewalSoon && (
                        <div className="mb-2 p-2 bg-yellow-50 rounded border border-yellow-200 text-yellow-700 text-sm">
                          <Calendar className="h-4 w-4 inline mr-1" />
                          Renews in {renewalDaysLeft} day{renewalDaysLeft !== 1 ? 's' : ''}
                        </div>
                      )}
                      
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-4 flex-wrap">
                          {item.billing_cost && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="h-3.5 w-3.5" />
                              ${item.billing_cost.toFixed(2)}
                              {item.billing_frequency && ` / ${item.billing_frequency}`}
                            </span>
                          )}
                          {item.renewal_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5" />
                              Renews: {new Date(item.renewal_date + 'T00:00:00').toLocaleDateString('en-US', { 
                                month: 'short', day: 'numeric', year: 'numeric' 
                              })}
                            </span>
                          )}
                        </div>
                        
                        {item.trial_end_date && (
                          <p className="text-yellow-700">
                            Trial ends: {new Date(item.trial_end_date + 'T00:00:00').toLocaleDateString('en-US', { 
                              month: 'short', day: 'numeric', year: 'numeric' 
                            })}
                          </p>
                        )}
                        
                        {/* Credentials - Masked */}
                        {(item.username_email || item.password_masked) && (
                          <div className="mt-2 p-2 bg-gray-50 rounded border">
                            {item.username_email && (
                              <p className="text-gray-700">
                                <span className="font-medium">Login:</span> {item.username_email}
                              </p>
                            )}
                            {item.password_masked && (
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-700">Password:</span>
                                <span className="font-mono">
                                  {showPasswords[item.id] ? item.password_masked : '••••••••'}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => togglePassword(item.id)}
                                  className="h-6 w-6 p-0"
                                >
                                  {showPasswords[item.id] ? (
                                    <EyeOff className="h-3.5 w-3.5" />
                                  ) : (
                                    <Eye className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {item.cancellation_date && (
                          <p className="text-gray-500">
                            Cancelled on: {new Date(item.cancellation_date + 'T00:00:00').toLocaleDateString()}
                          </p>
                        )}
                        
                        {item.notes && (
                          <p className="text-gray-500 italic mt-1">{item.notes}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <AddToCalendarButton
                        dateRecord={{
                          title: `${item.title} - ${item.membership_status === 'trial' ? 'Trial Ends' : 'Renewal'}`,
                          date: item.trial_end_date || item.renewal_date || item.date,
                          type: 'membership',
                          all_day: true,
                          reminder_minutes: 1440, // 1 day before
                          notes: `${item.billing_cost ? `$${item.billing_cost.toFixed(2)}/${item.billing_frequency || 'month'}` : ''}`
                        }}
                        size="sm"
                        variant="ghost"
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        data-testid={`add-to-calendar-${item.id}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AddDateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category="membership"
        onSuccess={handleDateAdded}
      />
    </>
  );
};

export default MembershipsTab;
